import { useEffect, useState } from "react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { PageHeader, EmptyState, formatDateTime } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { await apiFetch("/notifications/sweep", { method: "POST" }); } catch (_) {}
    const { data } = await supabase.from("notifications").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(100);
    setRows(data || []); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  const markRead = async (id) => {
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
    load();
  };
  const markAllRead = async () => {
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("user_id", user.id).is("read_at", null);
    toast.success("All notifications marked read");
    load();
  };

  /**
   * Notification `link` may be a full URL or a pathname. Also derive a target
   * from entity_type + entity_id when link is null so ALL notifications open
   * their underlying record — no dead-end notifications.
   */
  const resolveLink = (n) => {
    if (n.link) {
      try { const u = new URL(n.link, window.location.origin); return u.pathname + u.search; }
      catch (_) { return n.link; }
    }
    if (n.entity_type && n.entity_id) {
      const map = {
        clients: `/app/clients/${n.entity_id}`,
        tasks: `/app/tasks?highlight=${n.entity_id}`,
        appointments: `/app/calendar?highlight=${n.entity_id}`,
        documents: `/app/documents?highlight=${n.entity_id}`,
        invitations: `/app/team`,
      };
      return map[n.entity_type] || null;
    }
    if (n.client_id) return `/app/clients/${n.client_id}`;
    return null;
  };

  const openNotification = async (n) => {
    if (!n.read_at) {
      supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", n.id).then(load);
    }
    const target = resolveLink(n);
    if (target) navigate(target);
  };

  const filtered = filter === "unread" ? rows.filter((r) => !r.read_at) : rows;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="Everything that needs your attention."
        actions={rows.some((r) => !r.read_at) && (
          <Button data-testid="mark-all-read" variant="outline" className="rounded-full" onClick={markAllRead}><CheckCheck size={14} className="mr-1.5" /> Mark all as read</Button>
        )}
      />

      <div className="flex items-center gap-1 bg-white rounded-full border border-[#E8E2D9] p-1 w-fit">
        {["all", "unread"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`nfilter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${filter === s ? "bg-[#E8712C] text-white" : "text-[#57534E] hover:text-[#1C1917]"}`}>{s}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Bell} title="You're all caught up" description="You'll see appointment reminders, task updates and team activity here." />
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] divide-y divide-[#E8E2D9]">
          {filtered.map((n) => {
            const hasLink = resolveLink(n) !== null;
            return (
              <div key={n.id}
                   onClick={() => hasLink && openNotification(n)}
                   className={`px-6 py-4 flex items-start gap-3 transition-colors ${!n.read_at ? "bg-[#FDE9D8]/30" : ""} ${hasLink ? "cursor-pointer hover:bg-[#FAF8F5]" : ""}`}
                   data-testid={`notif-${n.id}`}>
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: n.read_at ? "transparent" : "#E8712C" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.body && <div className="text-sm text-[#57534E] mt-0.5">{n.body}</div>}
                  <div className="text-xs text-[#A8A29E] mt-1">{formatDateTime(n.created_at)}</div>
                </div>
                {hasLink && <span className="text-xs text-[#E8712C] whitespace-nowrap">Open →</span>}
                {!n.read_at && (
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); markRead(n.id); }} data-testid={`read-${n.id}`}>Mark read</Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
